import { useEffect, useMemo, useRef, useState } from "react";
import ParagraphText from "../ParagraphText";
import styles from "./styles.module.css";
import { useSnackbar } from "../../context/SnackbarContext";

const OPERATOR_RE = /[+\-*/×÷%]/;

const formatWithCommas = (raw) => {
  if (!raw) return "";
  const [intPart, decPart] = String(raw).split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

const displayIndexToRawIndex = (formatted, displayIndex) =>
  displayIndex - (formatted.slice(0, displayIndex).match(/,/g)?.length || 0);

const rawIndexToDisplayIndex = (raw, rawIndex) => {
  const formatted = formatWithCommas(raw);
  let rawSeen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (rawSeen === rawIndex) return i;
    if (formatted[i] !== ",") rawSeen++;
  }
  return formatted.length;
};

const stripPlaceholderPrefix = (placeholder) =>
  String(placeholder ?? "")
    .replace(/^Rs\.?\s*/i, "")
    .trim() || "500";

const safeEval = (expr) => {
  if (!expr) return null;
  try {
    const sanitized = expr
      .replace(/×/g, "*")
      .replace(/÷/g, "/")
      .replace(/\b0+(\d)/g, "$1"); // strip leading zeros
    if (!/^[\d+\-*/.()%]+$/.test(sanitized)) return null;
    const result = Function('"use strict";return(' + sanitized + ")")();
    if (isFinite(result) && !isNaN(result)) {
      return Math.round(result * 100) / 100;
    }
  } catch {}
  return null;
};

// Evaluates expression, stripping trailing operator if needed
const evaluate = (expr) => {
  if (!expr) return null;
  const direct = safeEval(expr);
  if (direct !== null) return direct;
  const trimmed = expr.replace(/[+\-*/×÷%]+$/, "");
  return safeEval(trimmed);
};

const CalcInput = ({
  value,
  onChange,
  name,
  label,
  infoLabel,
  placeholder = "Rs.500",
  baseClassname = "",
  inputId = "",
  inputRef: externalRef,
  error = { text: "", show: false },
  required = false,
  onKeyDown,
  formatDisplay = false,
}) => {
  const [expression, setExpression] = useState(
    value !== undefined && value !== null ? String(value) : "",
  );
  const { showSnackbar } = useSnackbar();
  const internalRef = useRef(null);
  const inputRef = externalRef || internalRef;
  const amountPlaceholder = stripPlaceholderPrefix(placeholder);

  // Always-fresh ref so useEffect can read latest expression without a dep
  const expressionRef = useRef(expression);
  expressionRef.current = expression;

  // Only reset when value changes to something our expression does NOT produce
  // (i.e. an external update like StockAddItem, not our own onChange call)
  useEffect(() => {
    const incoming = value !== undefined && value !== null ? String(value) : "";
    const currentExpr = expressionRef.current;
    const computed = evaluate(currentExpr);

    // Change came from us — computed result matches incoming
    if (computed !== null && String(computed) === incoming) return;
    // Plain number that already matches
    if (currentExpr === incoming) return;

    // Genuinely external change → reset
    setExpression(incoming);
  }, [value]);

  const showExpression = OPERATOR_RE.test(expression);

  const computedValue = useMemo(
    () => (showExpression ? evaluate(expression) : null),
    [expression, showExpression],
  );

  const formatDisplayValue = (raw) =>
    formatDisplay && raw && !OPERATOR_RE.test(raw)
      ? formatWithCommas(raw)
      : raw;

  // Input shows only the numeric expression; Rs. is rendered as a visual prefix.
  const displayText = showExpression
    ? computedValue !== null
      ? formatDisplayValue(String(computedValue))
      : ""
    : formatDisplayValue(expression);

  const commitExpression = (newExpr) => {
    setExpression(newExpr);
    if (!OPERATOR_RE.test(newExpr)) {
      onChange(name, newExpr || "");
      return;
    }
    const val = evaluate(newExpr);
    if (val !== null) onChange(name, String(val));
    else if (!newExpr) onChange(name, "");
  };

  const wouldCreateDivisionByZero = (expr, key) => {
  return /\/\s*$/.test(expr) && key === "0";
};

  const handleKeyDown = (e) => {
    const { key } = e;

    if (key === "Backspace") {
      e.preventDefault();
      if (!OPERATOR_RE.test(expression) && inputRef.current) {
        const selStart = inputRef.current.selectionStart;
        const selEnd = inputRef.current.selectionEnd;
        const formatted = formatDisplayValue(expression);
        const rawStart = formatDisplay
          ? displayIndexToRawIndex(formatted, selStart)
          : selStart;
        const rawEnd = formatDisplay
          ? displayIndexToRawIndex(formatted, selEnd)
          : selEnd;

        let newExpr;
        let newCursor;

        if (rawStart !== rawEnd) {
          newExpr = expression.slice(0, rawStart) + expression.slice(rawEnd);
          newCursor = rawStart;
        } else if (rawStart > 0) {
          newExpr =
            expression.slice(0, rawStart - 1) + expression.slice(rawStart);
          newCursor = rawStart - 1;
        } else {
          return;
        }

        commitExpression(newExpr);
        setTimeout(() => {
          const displayCursor = formatDisplay
            ? rawIndexToDisplayIndex(newExpr, newCursor)
            : newCursor;
          inputRef.current?.setSelectionRange(displayCursor, displayCursor);
        }, 0);
      } else {
        commitExpression(expression.slice(0, -1));
      }
      return;
    }

    if (key === "Enter" || key === "=") {
      e.preventDefault();
      if (computedValue !== null) {
        const strVal = String(computedValue);
        setExpression(strVal);
        onChange(name, strVal);
      }
      return;
    }

    if (/^[0-9.+\-*/×÷%]$/.test(key)) {
      e.preventDefault();

      if (wouldCreateDivisionByZero(expression, key)) {
        showSnackbar({
          message: "Cannot divide by zero",
          severity: "error",
        });
        return;
      }

      const lastChar = expression.slice(-1);
      const keyIsOp = OPERATOR_RE.test(key);
      const lastIsOp = OPERATOR_RE.test(lastChar);

      // Don't start with an operator (except minus for negatives)
      if (keyIsOp && key !== "-" && !expression) return;

      // Replace last operator instead of stacking two
      if (keyIsOp && lastIsOp) {
        commitExpression(expression.slice(0, -1) + key);
        return;
      }

      // Cursor-aware insertion when no operator in expression
      if (!OPERATOR_RE.test(expression) && !keyIsOp && inputRef.current) {
        const selStart = inputRef.current.selectionStart;
        const selEnd = inputRef.current.selectionEnd;
        const formatted = formatDisplayValue(expression);
        const rawStart = formatDisplay
          ? displayIndexToRawIndex(formatted, selStart)
          : selStart;
        const rawEnd = formatDisplay
          ? displayIndexToRawIndex(formatted, selEnd)
          : selEnd;
        const beforeCursor = expression.slice(0, rawStart);
        const afterCursor = expression.slice(rawEnd);

        let newExpr;

        // If current value is exactly "0" and user types a digit,
        // replace the 0 instead of creating "09", "05", etc.
        if (expression === "0" && /^[1-9]$/.test(key)) {
          newExpr = key;
        } else {
          newExpr = beforeCursor + key + afterCursor;
        }

        if (/^[0-9]$/.test(key)) {
          // Block leading zeros (e.g. "05")
          if (newExpr.length > 1 && newExpr[0] === "0" && newExpr[1] !== ".")
            return;

          // 12-digit cap
          if (newExpr.replace(".", "").length > 12) return;
        }

        if (key === ".") {
          // Block multiple dots
          if ((beforeCursor + afterCursor).includes(".")) return;
        }

        commitExpression(newExpr);
        const newRawPos = rawStart + 1;
        setTimeout(() => {
          const displayPos = formatDisplay
            ? rawIndexToDisplayIndex(newExpr, newRawPos)
            : newRawPos;
          inputRef.current?.setSelectionRange(displayPos, displayPos);
        }, 0);
        return;
      }

      // Validate digit input for current segment (operator mode)
      if (/^[0-9]$/.test(key)) {
        const segments = expression.split(OPERATOR_RE);
        const currentSegment = segments[segments.length - 1];
        if (currentSegment === "0") {
          if (expression === "0") {
            commitExpression(key);
            return;
          }
          return; // block digits after lone 0 (e.g. "500+05")
        }
        if (currentSegment.replace(".", "").length >= 12) return; // 12-digit cap
      }

      // Block multiple dots in the same number segment
      if (key === ".") {
        const segments = expression.split(OPERATOR_RE);
        if (segments[segments.length - 1].includes(".")) return;
      }

      commitExpression(expression + key);
    }
  };

  const handleBlur = () => {
    if (!expression) return;
    const trimmed = expression.replace(/[+\-*/×÷%]+$/, "");
    const val = safeEval(trimmed);
    if (val !== null && trimmed !== expression) {
      setExpression(trimmed);
      onChange(name, String(val));
    }
  };

  const isEmpty = !expression;

  return (
    <div className={`${baseClassname} ${styles.root} inputParentBaseStyle`}>
      {label && (
        <ParagraphText className="fontSize14 newFontColor InputLabelStyle mb-2">
          <span>
            {label} {required && <span className="foundationErrorRed">*</span>}
          </span>
          {infoLabel && (
            <ParagraphText className="fontSize10 foundationInk300">
              {infoLabel}
            </ParagraphText>
          )}
        </ParagraphText>
      )}

      <div className={`input_parent_base_style ${styles.inputWithPrefix}`}>
        <span
          className={`${styles.currencyPrefix} ${
            isEmpty ? styles.currencyPrefixPlaceholder : ""
          }`}
          aria-hidden="true"
        >
          Rs.
        </span>
        <input
          id={inputId}
          name={name}
          ref={inputRef}
          className={`input_base_style ${error?.show ? styles.errorBorder : ""}`}
          value={displayText}
          placeholder={amountPlaceholder}
          onChange={() => {}}
          onKeyDown={(e) => {
            handleKeyDown(e);
            onKeyDown?.(e);
          }}
          onBlur={handleBlur}
          autoComplete="off"
        />
      </div>

      {error?.show ? (
        <ParagraphText className={`inputError ${styles.inputError}`}>
          {error.text}
        </ParagraphText>
      ) : showExpression ? (
        <div className={styles.expressionLine}>{expression}</div>
      ) : (
        <ParagraphText className="inputError">&nbsp;</ParagraphText>
      )}
    </div>
  );
};

export default CalcInput;
